import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter, firstValueFrom, takeUntil } from 'rxjs';

import { ComponentWithDestroy } from 'src/app/modules/shared/component-with-destroy';
import { SidebarService } from 'src/app/services/sidebar.service';
import { BreadcrumbService } from 'src/app/services/breadcrumb.service';
import { DumbledoreService } from 'src/app/services/dumbledore.service';
import { HierarchyElementsService, CorpsService, BotsService } from 'src/app/services/firestore';
import { getSidebarItems, getBreadcrumbItems } from 'src/app/pages/portal/bot/utils';
import { BotModel } from 'src/app/models';
import { ClientEnvironmentService } from 'src/app/services/client-environment.service';

/** This component act as router outlet for bot configuration routes */
@Component({
  selector: 'app-bot-config-core-router-outlet',
  templateUrl: './router-outlet.component.html',
  styleUrls: ['./router-outlet.component.scss'],
})
export class RouterOutletComponent extends ComponentWithDestroy implements OnInit {
  private corpModel: any;
  private hierarchyElement: any;
  private botConfig: any;
  private botModel: BotModel;

  constructor(
    private readonly sidebarService: SidebarService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly dumbledoreService: DumbledoreService,
    private readonly hierarchyElementsService: HierarchyElementsService,
    private readonly corpsService: CorpsService,
    private readonly botService: BotsService,
    private readonly clientEnvironmentService: ClientEnvironmentService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    // Listen to navigation changes to update breadcrumbs
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.updateBreadcrumbs();
      });

    await this.loadContext();
    this.refreshUI();
  }

  private async loadContext(): Promise<void> {
    const corp = this.activatedRoute.snapshot.paramMap.get('corp');
    const hierarchyElementSystemName = this.activatedRoute.snapshot.paramMap.get('hierarchyElementSystemName');
    const bot = this.activatedRoute.snapshot.paramMap.get('bot');

    // Get corp
    this.corpModel = await firstValueFrom(this.corpsService.getCorpById(corp!));

    // Get hierarchy element
    const hierarchyElementId = `${corp}-${hierarchyElementSystemName}`;
    this.hierarchyElement = await firstValueFrom(this.hierarchyElementsService.getHierarchyElement(hierarchyElementId));

    // Get bot config
    const dealershipCode = `${corp}-${hierarchyElementSystemName}`;
    this.botConfig = await this.dumbledoreService.getBotConfigurationByDealershipCode(dealershipCode);

    this.botModel = (await this.botService.getBotBy(corp!, hierarchyElementSystemName!, bot!, (await firstValueFrom(this.clientEnvironmentService.items$))!))!;
  }

  private refreshUI(): void {
    const corp = this.activatedRoute.snapshot.paramMap.get('corp');
    const bot = this.activatedRoute.snapshot.paramMap.get('bot');

    // Set sidebar items
    const sidebarItems = getSidebarItems(corp!, this.hierarchyElement, bot!, this.botConfig?.knowledgeBase, this.botConfig?.voice_ai_enabled);

    this.sidebarService.set(sidebarItems);
    this.updateBreadcrumbs();
  }

  private updateBreadcrumbs(): void {
    // Set breadcrumbs if there's a child route
    const childRoute = this.activatedRoute.firstChild;
    if (childRoute && this.corpModel && this.hierarchyElement && this.botModel) {
      const botBreadcrumbTitle = childRoute.snapshot.data['botBreadcrumbTitle'];

      if (botBreadcrumbTitle) {
        const childPath = childRoute.snapshot.routeConfig?.path;
        const isBotActive = this.activatedRoute.routeConfig?.children?.some(r => r.path === '' && r.redirectTo === childPath) ?? false;
        this.breadcrumbService.set(getBreadcrumbItems(this.corpModel, this.hierarchyElement, this.botModel, botBreadcrumbTitle, '', isBotActive));
      }
    }
  }
}
